import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Link2, ExternalLink, Unlink } from "lucide-react";

interface LinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string, text?: string) => void;
  onRemove?: () => void;
  initialUrl?: string;
  initialText?: string;
  canRemove?: boolean;
}

export const LinkModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onRemove,
  initialUrl = "", 
  initialText = "",
  canRemove = false 
}: LinkModalProps) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);

  useEffect(() => {
    setUrl(initialUrl);
    setText(initialText);
  }, [initialUrl, initialText]);

  const handleSave = () => {
    if (url.trim()) {
      onSave(url.trim(), text.trim() || undefined);
      onClose();
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="border-[3px] border-dark">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            {canRemove ? "Modifica Link" : "Inserisci Link"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block font-heading text-sm mb-2">
              Testo da visualizzare
            </label>
            <BrutalInput
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Clicca qui per maggiori info"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Lascia vuoto per usare l'URL come testo
            </p>
          </div>

          <div>
            <label className="block font-heading text-sm mb-2">
              URL *
            </label>
            <BrutalInput
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://esempio.com"
              type="url"
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 mt-4">
          {canRemove && onRemove && (
            <BrutalButton
              type="button"
              variant="danger"
              onClick={handleRemove}
              className="mr-auto"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Rimuovi Link
            </BrutalButton>
          )}
          
          <BrutalButton
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Annulla
          </BrutalButton>
          
          <BrutalButton
            type="button"
            onClick={handleSave}
            disabled={!url.trim()}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Salva Link
          </BrutalButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
